import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, IconButton, Stack, Typography } from '@mui/material'
import { ConfirmationDialog } from 'Components/Dialog'
import { SectionLoader } from 'Components/Section'
import { IKoreanLeadingSchool } from 'Entities/koreanLeadingSchool'
import { EditPencilIcon } from 'Icons/Edit'
import { TrashIcon } from 'Icons/Trash'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { URL_KEYS } from 'Constants/Url'
import { useKoreanLanguage2 } from '../Hooks/useKoreanLanguageForm'
import { useKoreanLeadingSchools } from '../Hooks/useKoreanLeadingSchools'
import { KoreanLeadingSchoolEditDialog } from './KoreanLeadingSchoolEditDialog'

const getSchoolTitle = (school: IKoreanLeadingSchool, lang?: string | null) => {
  switch (lang) {
    case 'Uz':
      return school.schoolNameUz
    case 'Kr':
      return school.schoolNameKo
    case 'Ru':
    default:
      return school.schoolNameRu
  }
}

const getTypeLabel = (type: string) => {
  if (type === 'LEADING_SCHOOL') return 'Образцовая школа'
  if (type === 'KOREAN_CULTURE_SPECIALIZED_SCHOOL') return 'Школа углубленного изучения культуры Кореи'
  return type
}

export const KoreanLeadingSchoolList = () => {
  const { regions, isRegionsLoading, isRegionsFetching } = useKoreanLanguage2()
  const { leadingSchools, isLeadingSchoolsLoading, isLeadingSchoolsFetching, deleteLeadingSchoolMutation } = useKoreanLeadingSchools()

  const [searchParams] = useSearchParams()
  const lang = searchParams.get(URL_KEYS.LANG)

  const [expandedRegion, setExpandedRegion] = useState<number | null>(null)
  const [removeSchoolId, setRemoveSchoolId] = useState<number | null>(null)
  const [editSchool, setEditSchool] = useState<IKoreanLeadingSchool | null>(null)

  const regionSchools = useMemo(() => {
    if (expandedRegion == null) return []
    return leadingSchools.filter(school => school.regionCode === expandedRegion)
  }, [expandedRegion, leadingSchools])

  const handleAccordionChange = useCallback((regionCode: number) => {
    setExpandedRegion(prev => (prev === regionCode ? null : regionCode))
  }, [])

  const onRemove = useCallback(() => {
    if (removeSchoolId == null) return
    deleteLeadingSchoolMutation.mutate(removeSchoolId, {
      onSuccess: () => setRemoveSchoolId(null),
    })
  }, [deleteLeadingSchoolMutation, removeSchoolId])

  return (
    <>
      <ConfirmationDialog open={removeSchoolId != null} onAccept={onRemove} onClose={() => setRemoveSchoolId(null)} />

      {editSchool && <KoreanLeadingSchoolEditDialog open={!!editSchool} onClose={() => setEditSchool(null)} school={editSchool} />}

      <SectionLoader isLoading={isRegionsLoading} isFetching={isRegionsFetching}>
        <Grid container rowSpacing={2}>
          {regions.map((region: { regionCode: number; regionName: string }) => {
            const isExpanded = expandedRegion === region.regionCode
            const count = isExpanded ? regionSchools.length : null

            return (
              <Grid item xs={12} key={region.regionCode}>
                <Accordion expanded={isExpanded} onChange={() => handleAccordionChange(region.regionCode)}>
                  <AccordionSummary>
                    <Stack direction='row' justifyContent='space-between' alignItems='center' width='100%' pr={2}>
                      <Typography fontWeight={700}>{region.regionName}</Typography>
                      <Stack direction='row' spacing={1.5}>
                        {isExpanded && (
                          <Typography variant='body2' color='text.secondary'>
                            Школ: {count}
                          </Typography>
                        )}
                        <Typography variant='body2' color='text.secondary'>
                          지역 코드: {region.regionCode}
                        </Typography>
                      </Stack>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    {isExpanded && (
                      <SectionLoader isLoading={isLeadingSchoolsLoading} isFetching={isLeadingSchoolsFetching}>
                        <Stack spacing={1.5}>
                          {regionSchools.length ? (
                            regionSchools.map(school => (
                              <Box
                                key={school.leadingSchoolId}
                                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2, py: 1.5 }}>
                                <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
                                  <Box>
                                    <Typography fontWeight={600}>{getSchoolTitle(school, lang)}</Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                      {getTypeLabel(school.type)} · ID: {school.leadingSchoolId}
                                    </Typography>
                                  </Box>
                                  <Stack direction='row' spacing={1}>
                                    <IconButton onClick={() => setEditSchool(school)}>
                                      <EditPencilIcon />
                                    </IconButton>
                                    <IconButton onClick={() => setRemoveSchoolId(school.leadingSchoolId)}>
                                      <TrashIcon />
                                    </IconButton>
                                  </Stack>
                                </Stack>
                              </Box>
                            ))
                          ) : (
                            <Typography color='text.secondary'>등록된 학교가 없습니다.</Typography>
                          )}
                        </Stack>
                      </SectionLoader>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )
          })}
        </Grid>
      </SectionLoader>
    </>
  )
}
